import { apiPost } from '../client';
import { LONG_TIMEOUT_MS } from '../../config/env';
import type { Contact } from '../types/common';
import type {
  HotelAllocateRequest,
  HotelAllocateResponse,
  HotelCancelPenaltyRequest,
  HotelCancelPenaltyResponse,
  HotelFacets,
  HotelFilterRequest,
  HotelFilterResponse,
  HotelLocationSearchRequest,
  HotelLocationSearchResponse,
  HotelMakeReservationRequest,
  HotelMakeReservationResponse,
  HotelOfferDetailRequest,
  HotelOfferDetailResponse,
  HotelPassengerDetail,
  HotelProductInfoResponse,
  HotelReservationCancelRequest,
  HotelReservationCancelResponse,
  HotelSearchRequest,
  HotelSearchResponse,
  HotelUpdatePassengerRequest,
  HotelUpdatePassengerResponse,
  PaximumGetProductInfoRequest,
  PaymentRequest,
  PaymentResponse,
} from '../types/hotel';

/** Lifts the aggregate filter facets off the OUTER `HotelFilterResponse` (the fields
 *  `searchHotels`/`getHotelRooms` historically discarded) into the flat `HotelFacets`
 *  shape `HotelSearchResponse.filters` carries. Every field stays nullable — a region
 *  with no results comes back with all of them `null`. */
function pickHotelFacets(res: HotelFilterResponse): HotelFacets {
  return {
    hotelPropertiesList: res.hotelPropertiesList,
    hotelServicesList: res.hotelServicesList,
    roomPropertiesList: res.roomPropertiesList,
    starGroups: res.starGroups,
    minPrice: res.minPrice,
    maxPrice: res.maxPrice,
    uniqueHotelList: res.uniqueHotelList,
  };
}

/** Lokasyon otomatik tamamlama. `key` drives the match; `location` is required by the
 *  validator but empirically unused — see comment on HotelLocationSearchRequest. */
export function searchHotelLocations(query: string): Promise<HotelLocationSearchResponse> {
  return apiPost<HotelLocationSearchRequest, HotelLocationSearchResponse>('Hotel/SearchLocation', {
    key: query,
    location: query,
  });
}

/**
 * Otel arama — `Hotel/Search` yerine `Hotel/GetFilter` çağırıyor: aynı `HotelSearchRequest`
 * body'sini kabul ediyor, ama `Hotel/Search` bölge (locationType:1) aramalarında canlı
 * olarak boş `priceResults` dönerken `Hotel/GetFilter` aynı istekle gerçek sonuç
 * döndürdüğü doğrulandı (36 otel, 2 sayfa — Alexandroupoli/GR). `GetFilter`'ın asıl
 * sonuçları `hotelSearchResponse` alanının içinde geliyor, burada onu çıkarıp
 * `HotelSearchResponse` şeklinde dönüyoruz ki flowConfig/ResultsStep hiç değişmesin —
 * ama üst seviyedeki `searchId`'yi (nested `hotelSearchResponse`'ta YOK, sadece burada
 * var, confirmed) `HotelSearchResponse.searchId` alanına damgalayarak koruyoruz, çünkü
 * `Hotel/GetFiltersFromCollaction` drill-down'ı (getHotelRooms) aynı arama oturumuna
 * bağlanmak için bunu gerektiriyor. `GetFilter`'ın ek döndürdüğü
 * `hotelPropertiesList`/`hotelServicesList`/`roomPropertiesList`/`starGroups`/`minPrice`/
 * `maxPrice`/`uniqueHotelList` facet'leri artık atılmıyor — `pickHotelFacets` ile
 * `HotelSearchResponse.filters` altına taşınıp `HotelFilterPanel`'e (sunucu-taraflı
 * yıldız/fiyat/tesis filtresi) besleniyor.
 */
export function searchHotels(request: HotelSearchRequest): Promise<HotelSearchResponse> {
  return apiPost<HotelFilterRequest, HotelFilterResponse>('Hotel/GetFilter', request, {
    timeoutMs: LONG_TIMEOUT_MS,
  }).then((res) => ({
    ...(res.hotelSearchResponse ?? { hasError: res.hasError, serviceError: res.serviceError }),
    searchId: res.searchId,
    filters: pickHotelFacets(res),
  }));
}

/** Oda/fiyat rezervasyona alma — `rateId` is the only validator-required field; the
 *  rest of `request` is sent best-effort as context (see HotelAllocateRequest comment). */
export function allocateHotel(request: HotelAllocateRequest): Promise<HotelAllocateResponse> {
  return apiPost<HotelAllocateRequest, HotelAllocateResponse>('Hotel/Allocate', request);
}

/** Misafir bilgisi güncelleme. */
export function updateHotelPassengers(
  shoppingCartId: string,
  passengers: HotelPassengerDetail[],
  extra?: { contact?: Contact; hotelName?: string; message?: string }
): Promise<HotelUpdatePassengerResponse> {
  return apiPost<HotelUpdatePassengerRequest, HotelUpdatePassengerResponse>('Hotel/UpdatePassenger', {
    shoppingCartId,
    passengers,
    ...extra,
  });
}

/** Rezervasyonu kesinleştirme (Hotel'in FinalizeShopping karşılığı). */
export function makeHotelReservation(
  request: HotelMakeReservationRequest
): Promise<HotelMakeReservationResponse> {
  return apiPost<HotelMakeReservationRequest, HotelMakeReservationResponse>(
    'Hotel/MakeReservation',
    request,
    { timeoutMs: LONG_TIMEOUT_MS }
  );
}

/** Rezervasyon iptali. */
export function cancelHotelReservation(
  reservationNumber: string,
  reason: string
): Promise<HotelReservationCancelResponse> {
  return apiPost<HotelReservationCancelRequest, HotelReservationCancelResponse>(
    'Hotel/GetReservationCancel',
    { reservationNumber, reason }
  );
}

/**
 * Teklif detayı — bir oda/fiyat seçildikten sonra, `Hotel/Allocate`'ten hemen önce
 * çağrılır: seçilen teklifin GÜNCEL `servicePrice` + `cancellationPolicy` (çok satırlı
 * "Due Date … Penalty …") + `dueDate` değerlerini döner. Arama sonucundaki fiyattan
 * farklıysa `ReservationStep` bloke etmeyen bir "Fiyat güncellendi" uyarısı gösterir.
 */
export function getHotelOfferDetails(
  params: { offerId: string; currency?: string }
): Promise<HotelOfferDetailResponse> {
  return apiPost<HotelOfferDetailRequest, HotelOfferDetailResponse>('Hotel/GetOfferDetails', {
    offerId: params.offerId,
    currency: params.currency,
  });
}

/**
 * İptal cezası ön-sorgusu — `Hotel/GetReservationCancel` ÇAĞRILMADAN önce, iptal onay
 * diyaloğunda ceza kırılımını (`body.cancelPenalties[]`) göstermek için kullanılır.
 * Girdi backend'in paylaşılan `CoperReservationLoadRequest`'i (`Hotel/ReservationLoad`
 * ile aynı); yalnız `PNR_No` zorunlu. Cevap `ResponseBase` değil ama aynı
 * `hasError`/`serviceError` çiftini taşıdığı için `apiPost`'un zarf kontrolü çalışır —
 * hata durumunda çağıran taraf `ApiError`'ı yakalayıp diyaloğu ceza kırılımı olmadan
 * ("ceza bilgisi alınamadı") gösterir.
 */
export function getHotelReservationCancelPenalty(
  pnrNo: string,
  opts?: { language?: string; currencyType?: string; shoppingCartId?: string }
): Promise<HotelCancelPenaltyResponse> {
  return apiPost<HotelCancelPenaltyRequest, HotelCancelPenaltyResponse>(
    'Hotel/GetReservationCancelPenalty',
    {
      PNR_No: pnrNo,
      language: opts?.language,
      currencyType: opts?.currencyType,
      shoppingCartId: opts?.shoppingCartId,
      getFromShoppingCart: opts?.shoppingCartId ? true : undefined,
    }
  );
}

/** Otel ürün bilgisi (Paximum) — otel/oda/servis özellik listeleri, otel/oda görselleri,
 *  adres/konum. "Detayları Göster" ile bir otel kartı genişletildiğinde çağrılır (bkz.
 *  ResultsStep.tsx). Request/response şekli backend'in kendi C# sınıflarıyla doğrulandı. */
export function getHotelProductInfo(hotelId: string, arkmanHotel: boolean): Promise<HotelProductInfoResponse> {
  return apiPost<PaximumGetProductInfoRequest, HotelProductInfoResponse>('Hotel/GetProductInfo', {
    hotelId,
    arkmanHotel,
  });
}

/**
 * Belirli bir otelin TÜM oda/fiyat seçeneklerini çeker — `Hotel/Search`/`Hotel/GetFilter`'ın
 * bölge aramasındaki sayfalanmış/kısmi sonuçlarının aksine, bu otele özel tam liste döner.
 * `Hotel/GetFiltersFromCollaction`'a `HotelSearchRequest` gönderiyor; backend'in kendi
 * (paylaşılan) request-kurma kodundan doğrulanan alan geçersiz kılmaları:
 * `language:'tr'`, `hotelName` (Hotel/GetProductInfo'nun döndürdüğü isim), `searchId`
 * (orijinal aramadan taşınır — aynı sunucu tarafı arama/cache oturumuna bağlar),
 * `refoundble` (orijinal istekten taşınır), `isSuggestedSearch:true`, ve — kritik —
 * `locationKey` VE `rateId` otelin kendi `rateId`'sine (== hotelId, doğrulandı) ayarlanır;
 * böylece bölge yerine tek bir otel hedeflenmiş olur. `baseRequest`'in geri kalanı (tarih,
 * yolcu, döviz, uyruk, paging vb.) değişmeden taşınır.
 */
export function getHotelRooms(
  baseRequest: HotelSearchRequest,
  params: { rateId: string; hotelName?: string; searchId?: string }
): Promise<HotelSearchResponse> {
  const request: HotelFilterRequest = {
    ...baseRequest,
    language: 'tr',
    hotelName: params.hotelName,
    searchId: params.searchId ?? baseRequest.searchId,
    refoundble: baseRequest.refoundble ?? 0,
    isSuggestedSearch: true,
    locationKey: params.rateId,
    rateId: params.rateId,
  };
  return apiPost<HotelFilterRequest, HotelFilterResponse>('Hotel/GetFiltersFromCollaction', request, {
    timeoutMs: LONG_TIMEOUT_MS,
  }).then((res) => ({
    ...(res.hotelSearchResponse ?? { hasError: res.hasError, serviceError: res.serviceError }),
    searchId: res.searchId,
    filters: pickHotelFacets(res),
  }));
}

/** Ödeme — shared route (not Hotel-prefixed), duplicated locally per module scope boundary. */
export function makePayment(request: PaymentRequest): Promise<PaymentResponse> {
  return apiPost<PaymentRequest, PaymentResponse>('MakePayment', request, {
    timeoutMs: LONG_TIMEOUT_MS,
  });
}
